import React from 'react';

export interface MenuItemBase {
    id: string;
    disabled?: boolean;
}

export interface MenuItemAction extends MenuItemBase {
    type: 'action';
    label: React.ReactNode;
    icon?: React.ReactNode;
    hint?: string;
    onClick: () => void;
}

export interface MenuItemRadio extends MenuItemBase {
    type: 'radio';
    label: React.ReactNode;
    icon?: React.ReactNode;
    checked: boolean;
    onClick: () => void;
}

export interface MenuItemCheckbox extends MenuItemBase {
    type: 'checkbox';
    label: React.ReactNode;
    icon?: React.ReactNode;
    checked: boolean;
    onClick: () => void;
}

export interface MenuItemSubmenu extends MenuItemBase {
    type: 'submenu';
    label: React.ReactNode;
    icon?: React.ReactNode;
    children: MenuItem[];
    layout?: 'list' | 'grid';
    gridColumns?: number;
}

export interface MenuItemCustom extends MenuItemBase {
    type: 'custom';
    render: (props: { focused: boolean }) => React.ReactNode;
    onClick?: () => void;
}

export interface MenuSeparator {
    type: 'separator';
    id: string;
}

export interface MenuSectionLabel {
    type: 'label';
    id: string;
    text: string;
}

export type MenuItem =
    | MenuItemAction
    | MenuItemRadio
    | MenuItemCheckbox
    | MenuItemSubmenu
    | MenuItemCustom
    | MenuSeparator
    | MenuSectionLabel;

export interface MenuProps {
    items: MenuItem[];
    isOpen: boolean;
    onClose: () => void;
    ariaLabel: string;
    triggerRef?: React.RefObject<HTMLElement | null>;
    className?: string;
    anchorOrigin?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
    layout?: 'list' | 'grid';
    gridColumns?: number;
    isSubmenu?: boolean;
    onSubmenuClose?: () => void;
}

/**
 * Helper that prepends a section label and appends a separator.
 * Useful for grouping menu items with a heading.
 */
export function menuSection(label: string, items: MenuItem[]): MenuItem[] {
    const labelId = `section-label-${label.toLowerCase().replace(/\s+/g, '-')}`;
    const separatorId = `section-sep-${label.toLowerCase().replace(/\s+/g, '-')}`;
    return [
        { type: 'label' as const, id: labelId, text: label },
        ...items,
        { type: 'separator' as const, id: separatorId },
    ];
}
